import { useState, useEffect, useRef, useCallback } from 'react';
import { useFeedback } from '@/app/providers/FeedbackContext';
import { SetupScreenStyles } from './SetupScreenStyles';
import { usePromptCarousel } from './usePromptCarousel';
import {
  POLL_INTERVAL_MS,
  SLOW_SETUP_MS,
  HARD_TIMEOUT_MS,
  MIN_VISIBLE_MS,
  STEP_1_DONE_AT_MS,
  STEP_2_DONE_AT_MS,
  FADE_OUT_DELAY_MS,
} from './workspaceSetup.constants';

// Re-exported so the existing `import { SetupScreenStyles } from
// ".../WorkspaceSetupScreen"` consumers (e.g. features/chat ChatBox) keep
// working after the styles moved into their own sibling file.
export { SetupScreenStyles };

interface SetupStep {
  label: string;
  done: boolean;
}

interface WorkspaceSetupScreenProps {
  onReady: () => void;
  /** Optional — fires only on the HARD_TIMEOUT_MS ceiling. Kept optional so
   *  callers that don't care about timeouts don't have to pass a stub. */
  onTimeout?: () => void;
  checkReady: () => Promise<boolean>;
}

export function WorkspaceSetupScreen({
  onReady,
  onTimeout,
  checkReady,
}: WorkspaceSetupScreenProps) {
  const [steps, setSteps] = useState<SetupStep[]>([
    { label: 'Creating your workspace', done: false },
    { label: 'Setting up your modules', done: false },
    { label: 'Preparing your assistant', done: false },
  ]);
  const [timedOut, setTimedOut] = useState(false);
  const [isSlowSetup, setIsSlowSetup] = useState(false);
  const { currentPrompt, fadeClass, prompts } = usePromptCarousel();
  const elapsedRef = useRef(0);
  const readyRef = useRef(false);
  const completedRef = useRef(false);
  const mountedAtRef = useRef(Date.now());
  const slowSetupReportedRef = useRef(false);
  const { openWithContext } = useFeedback();

  // Diagnostic packet sent to Slack via the existing /feedback endpoint when
  // a stuck user clicks "Report this issue" on the timeout screen. The user
  // is authenticated by this point, so the server attaches their identity.
  // Stack field gets the nice code-block render in Slack — see
  // apps/api/src/routes/feedback.ts.
  const handleReportIssue = useCallback(() => {
    const timeOnScreenSec = Math.round((Date.now() - mountedAtRef.current) / 1000);
    const lastIncompleteStep = steps.find((s) => !s.done)?.label ?? '(none)';
    const thresholdSec = Math.round((timedOut ? HARD_TIMEOUT_MS : SLOW_SETUP_MS) / 1000);
    const statusLabel = timedOut ? 'timed out' : 'still pending';
    openWithContext('error_report', {
      errorInfo: {
        message: `Workspace setup ${statusLabel} after ${thresholdSec}s — last incomplete step: "${lastIncompleteStep}"`,
        stack: [
          'Source: WorkspaceSetupScreen (workspace bootstrap timeout)',
          `Path: ${window.location.pathname}${window.location.search}`,
          `URL: ${window.location.href}`,
          `User agent: ${navigator.userAgent}`,
          `Viewport: ${window.innerWidth}×${window.innerHeight}`,
          `Language: ${navigator.language}`,
          `Online: ${navigator.onLine}`,
          `Time on screen: ${timeOnScreenSec}s`,
          `Steps state: ${steps.map((s) => `${s.label}=${s.done ? 'done' : 'pending'}`).join(' | ')}`,
        ].join('\n'),
      },
    });
  }, [openWithContext, steps, timedOut]);

  // Polling + step cadence + readiness gate
  useEffect(() => {
    const interval = setInterval(async () => {
      elapsedRef.current += POLL_INTERVAL_MS;

      if (elapsedRef.current >= HARD_TIMEOUT_MS) {
        clearInterval(interval);
        setTimedOut(true);
        onTimeout?.();
        return;
      }

      // At SLOW_SETUP_MS, flip the non-fatal banner so the user knows we're
      // still working. Polling continues all the way to HARD_TIMEOUT_MS.
      if (elapsedRef.current >= SLOW_SETUP_MS && !slowSetupReportedRef.current) {
        slowSetupReportedRef.current = true;
        setIsSlowSetup(true);
      }

      // Step completion is now tied to readiness (no more time-based theater).
      if (elapsedRef.current >= STEP_1_DONE_AT_MS) {
        setSteps((prev) => (prev[0].done ? prev : [{ ...prev[0], done: true }, prev[1], prev[2]]));
      }
      if (elapsedRef.current >= STEP_2_DONE_AT_MS) {
        setSteps((prev) => (prev[1].done ? prev : [prev[0], { ...prev[1], done: true }, prev[2]]));
      }

      try {
        const ready = await checkReady();
        if (ready) readyRef.current = true;
      } catch {
        // Keep polling on error
      }

      // Complete as soon as the backend is ready (modules exist) + tiny grace.
      // No long artificial delays — we want the user in chat with the first
      // greeting instantly.
      const minVisibleElapsed = Date.now() - mountedAtRef.current >= MIN_VISIBLE_MS;
      if (readyRef.current && minVisibleElapsed && !completedRef.current) {
        completedRef.current = true;
        clearInterval(interval);
        setSteps((prev) => prev.map((s) => ({ ...s, done: true })));
        setTimeout(onReady, FADE_OUT_DELAY_MS);
      }
    }, POLL_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [checkReady, onReady, onTimeout]);

  if (timedOut) {
    return (
      <div className="setup-screen">
        <div className="setup-content">
          <h2>Setup could not finish</h2>
          <p>Please refresh and try again. If it keeps happening, let us know.</p>
          <div className="setup-actions">
            <button
              onClick={() => window.location.reload()}
              className="setup-retry-btn"
            >
              Refresh
            </button>
            <button
              onClick={handleReportIssue}
              className="setup-report-btn"
              type="button"
            >
              Report this issue
            </button>
          </div>
        </div>
        <SetupScreenStyles />
      </div>
    );
  }

  return (
    <div className="setup-screen">
      <div className="setup-content">
        <div className="setup-logo" aria-hidden="true">
          <div className="setup-logo-glow" />
          <img src="/favicon.svg" alt="" className="setup-logo-img" />
        </div>
        <h2>Setting up your workspace</h2>

        <div className="setup-steps">
          {steps.map((step, i) => (
            <div key={i} className={`setup-step ${step.done ? 'done' : ''}`}>
              <span className="step-icon" aria-hidden="true">
                {step.done ? '✓' : '○'}
              </span>
              <span className="step-label">{step.label}…</span>
            </div>
          ))}
        </div>

        {isSlowSetup && (
          <div className="setup-slow-note" role="status">
            <p>Still warming up &mdash; no refresh needed. I&rsquo;ll keep checking.</p>
            <button
              onClick={handleReportIssue}
              className="setup-report-btn"
              type="button"
            >
              Report this issue
            </button>
          </div>
        )}

        <div className="setup-divider" />

        {/* Named workspace-setup-prompts (not setup-suggestions) to avoid
            colliding with the global `.setup-suggestions` rule in
            IntegrationsModal.css — see #2150. */}
        <div className="workspace-setup-prompts">
          <p className="suggestions-label">Things you can ask Xavior</p>
          <div className={`suggestion-bubble ${fadeClass}`}>
            &ldquo;{prompts[currentPrompt]}&rdquo;
          </div>
        </div>
      </div>
      <SetupScreenStyles />
    </div>
  );
}
